import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Drawer,
  Form,
  Input,
  Select,
  Popconfirm,
  Empty,
} from "antd";
import { ShopOutlined, PlusOutlined } from "@ant-design/icons";

import {
  getSuppliers,
  createSupplier,
  updateSupplier,
  deleteSupplier as deleteSupplierAPI,
} from "../../api/supplierApi";
import { notify } from "../components/Customs/NotifyProvider";
import {
  canViewModule,
  canCreateModule,
  canUpdateModule,
  canDeleteModule,
} from "../utils/permissionUtils";

const { Option } = Select;
const MODULE_KEY = "supplier";

const Supplier = () => {
  const hasViewAccess = canViewModule(MODULE_KEY);
  const [suppliers, setSuppliers] = useState([]);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [form] = Form.useForm();

  // ---------------- INITIAL DATA ----------------
  const fetchSuppliers = async () => {
  try {
    const res = await getSuppliers();
    setSuppliers(res.data);
  } catch (err) {
    console.error("FETCH ERROR:", err);
  }
};

useEffect(() => {
  if (!hasViewAccess) return;
  fetchSuppliers();
}, [hasViewAccess]);
  // ---------------- OPEN DRAWER ----------------
  const openDrawer = (record = null) => {
    if (record && !canUpdateModule(MODULE_KEY)) {
      notify.error("Permission Denied", "No permission to update supplier");
      return;
    }

    if (!record && !canCreateModule(MODULE_KEY)) {
      notify.error("Permission Denied", "No permission to create supplier");
      return;
    }

    setEditingSupplier(record);
    setIsDrawerOpen(true);

    if (record) {
      form.setFieldsValue(record);
    } else {
      form.resetFields();
    }
  };

  // ---------------- CLOSE DRAWER ----------------
  const closeDrawer = () => {
    setIsDrawerOpen(false);
    setEditingSupplier(null);
    form.resetFields();
  };

  // ---------------- SAVE ----------------
const handleSave = async (values) => {
  if (editingSupplier) {
    if (!canUpdateModule(MODULE_KEY)) {
      notify.error("Permission Denied", "No permission to update supplier");
      return;
    }
  } else if (!canCreateModule(MODULE_KEY)) {
    notify.error("Permission Denied", "No permission to create supplier");
    return;
  }

  try {
    if (editingSupplier) {
      await updateSupplier(editingSupplier.id, values);
      notify.success("Supplier updated successfully");
    } else {
      await createSupplier(values);
      notify.success("Supplier created successfully");
    }

    await fetchSuppliers();
    closeDrawer();
  } catch (err) {
    notify.error(err?.response?.data?.message || "Failed to save supplier");
    console.error("SAVE ERROR:", err);
  }
};

  // ---------------- DELETE ----------------
const handleDelete = async (id) => {
  if (!canDeleteModule(MODULE_KEY)) {
    notify.error("Permission Denied", "No permission to delete supplier");
    return;
  }

  try {
    await deleteSupplierAPI(id);
    notify.success("Supplier deleted successfully");
    await fetchSuppliers();
  } catch (err) {
    notify.error(err?.response?.data?.message || "Failed to delete supplier");
    console.error("DELETE ERROR:", err);
  }
};

  // ---------------- MAIN TABLE ----------------
  const columns = [
    { title: "Supplier Name", dataIndex: "name" },
    { title: "Phone", dataIndex: "phone" },
    { title: "Address", dataIndex: "address" },
    { title: "PAN", dataIndex: "pan" },
    { title: "License", dataIndex: "license" },
    { title: "Aadhaar", dataIndex: "aadhar" },
    { title: "GST", dataIndex: "gst" },
    { title: "Status", dataIndex: "status" },// status added//
    

    {
      title: "Action",
      render: (_, record) => (
        <>
          <Button
            type="link"
            onClick={() => openDrawer(record)}
            disabled={!canUpdateModule(MODULE_KEY)}
          >
            Edit
          </Button>

          <Popconfirm
            title="Delete supplier?"
            onConfirm={() => handleDelete(record.id)}
          >
            <Button type="link" danger disabled={!canDeleteModule(MODULE_KEY)}>
              Delete
            </Button>
          </Popconfirm>
        </>
      ),
    },
  ];

  if (!hasViewAccess) {
    return (
      <div className="page-shell user-master-page">
        <div className="page-empty">
          <Empty description="No permission to view supplier page" />
        </div>
      </div>
    );
  }

  return (
    <div className="page-shell user-master-page">
      <div className="user-page-header">
        <div className="user-page-header-left">
          <div className="user-page-icon">
            <ShopOutlined />
          </div>

          <div>
            <h2 className="user-page-title">Supplier Management</h2>
            <p className="user-page-subtitle">
             Centrally create, update, and manage all drink suppliers from a single, unified interface.
            </p>
          </div>
        </div>

        <div className="user-page-header-actions">
          <Button
            type="primary"
            className="primary-btn"
            icon={<PlusOutlined />}
            onClick={() => openDrawer()}
            disabled={!canCreateModule(MODULE_KEY)}
          >
            Create Supplier
          </Button>
        </div>
      </div>

      <div className="page-card table-card">
        <div className="table-card-header">
          <div>
            <h3>Supplier List</h3>
            <p>View and manage all available suppliers.</p>
          </div>
        </div>

        <Table dataSource={suppliers} columns={columns} rowKey="id" />
      </div>

      <Drawer
        title={editingSupplier ? "Edit Supplier" : "Add Supplier"}
        placement="right"
        width={420}
        onClose={closeDrawer}
        open={isDrawerOpen}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleSave}>
          <div className="page-card table-card">
            <div className="table-card-header">
              <div>
                <h3>
                  {editingSupplier
                    ? "Update Supplier Details"
                    : "Create Supplier Details"}
                </h3>
                <p>Fill the details below to save the supplier.</p>
              </div>
            </div>

            <Form.Item
              name="name"
              label="Supplier Name"
              rules={[{ required: true }]}
            >
              <Input placeholder="Enter supplier name" />
            </Form.Item>

            <Form.Item name="phone" label="Phone">
              <Input placeholder="Enter phone number" />
            </Form.Item>

            <Form.Item name="address" label="Address">
              <Input placeholder="Enter supplier address" />
            </Form.Item>

            <Form.Item name="pan" label="PAN Number">
              <Input placeholder="Enter PAN number" />
            </Form.Item>

            <Form.Item name="license" label="License Number">
              <Input placeholder="Enter license number" />
            </Form.Item>

            <Form.Item name="aadhar" label="Aadhaar Number">
              <Input placeholder="Enter Aadhaar number" />
            </Form.Item>

            <Form.Item name="gst" label="GST">
              <Input placeholder="Enter GST" />
            </Form.Item>


            <Form.Item name="status" label="Status">
              <Select placeholder="Select status">
                <Option value="Active">Active</Option>
                <Option value="Inactive">Inactive</Option>
              </Select>
            </Form.Item>

            <div className="form-actions">
              <Button className="secondary-btn" onClick={closeDrawer}>
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                className="primary-btn"
                disabled={
                  editingSupplier
                    ? !canUpdateModule(MODULE_KEY)
                    : !canCreateModule(MODULE_KEY)
                }
              >
                {editingSupplier ? "Update Supplier" : "Save Supplier"}
              </Button>
            </div>
          </div>
        </Form>
      </Drawer>
    </div>
  );
};

export default Supplier;
